import axios from 'axios'
import { updateStoreMembers } from '~/utils'
import i18n from '../i18n/index'

const referralFields = [
    'id',
    'toUser(id,designation,firstName,lastName,userRole(id,name),email,phone,avatar(thumb-360,source),specialities(speciality(id,name),subSpeciality(id,name)))',
    'fromUser(id,designation,firstName,lastName,userRole(id,name),email,phone,practiceType,avatar(thumb-360,source),specialities(speciality(id,name),subSpeciality(id,name)))',
    'note',
    'primaryCarePhysician',
    'createdDateTime',
    'appointmentDateTime',
    'approveDateTime',
    'state',
    'status',
    'diagnosis',
    'insurancePlan(id,name)',
    'isInsuranceAuthorizationRequired',
    'isPrimaryCareOutsideMedmatch',
    'patient(id,firstName,lastName,mobilePhone,dateOfBirth,email,address,city,state,zip)',
    'insuranceAuthorizationObtainedDateTime',
    'consultationNoteDateTime',
    'fromReferralId',
    'physicianReview(id,ratings,createdDateTime)',
    'patientReview(id,ratings,createdDateTime)',
    'consultationReports(note,url,extension)',
    'referralNotes(note,url,extension)',
    'attachments(id,fileName,note,url,extension)',
    'speciality',
    'practiceLocation(zip,address,name,city,state)',
    'shouldShowPatientInfo',
    'closeReason',
    'closeComment',
    'patientStatusDateTime',
    'notesRequestDateTime',
    'insuranceAuthNumber',
    'insuranceGroupNumber',
    'insuranceNumber',
    'practice(id,name,practiceType,email)',
    'fromPractice(id,name,practiceType,email)',
    'facilityServiceTests(id,name,group(id,name))',
    'proposedAvailabilitySlots',
    'stageInfo(stage,nextAction,hoursLeft,urgency,providersWithSlots,providersWithoutSlots,totalProviders,pendingProviders,externalRecipientsCount,targetPracticeType,additionalRecordsRequests)',
    'sourceType',
    'telehealth',
    'followUpReferral',
]

export default {
    getList(type, userId, query, fields) {
        const _fields = (
            fields || [
                'id',
                'toUser(id,designation,firstName,lastName,userRole(id,name),avatar(thumb-360,source),specialities(speciality(id,name),subSpeciality(id,name)))',
                'fromUser(id,designation,firstName,lastName,userRole(id,name),avatar(thumb-360,source),specialities(speciality(id,name),subSpeciality(id,name)))',
                'createdDateTime',
                'appointmentDateTime',
                'approveDateTime',
                'patientStatusDateTime',
                'state',
                'status',
                'practiceLocation(name)',
                'patient(firstName,lastName,dateOfBirth,email,mobilePhone,address,city,state,zip)',
                'shouldShowPatientInfo',
                'practice(id,name,practiceType,image)',
                'fromPractice(id,name,practiceType,email)',
                'facilityServiceTests(id,name,group(id,name))',
            ]
        ).join(',')

        const params = {
            _fields,
            ...query,
        }

        params[type === 'received' ? 'toUserId' : 'fromUserId'] = userId
        if (params.state === 'all') delete params.state

        return axios.get('referrals', { params })
    },
    getListV2(query, fields) {
        const _fields = (
            fields || [
                'id',
                'sourceType',
                'toUser(id,designation,firstName,lastName,userRole(id,name),email,phone,mobilePhone,avatar(thumb-360,source),specialities(speciality(id,name),subSpeciality(id,name)))',
                'fromUser(id,designation,firstName,lastName,userRole(id,name),email,phone,mobilePhone,practiceType,avatar(thumb-360,source),specialities(speciality(id,name),subSpeciality(id,name)))',
                'createdDateTime',
                'appointmentDateTime',
                'approveDateTime',
                'patientStatusDateTime',
                'state',
                'status',
                'practiceLocation(name,address,state,zip)',
                'patient(firstName,lastName,dateOfBirth,email,mobilePhone,address,city,state,zip)',
                'shouldShowPatientInfo',
                'practice(id,name,practiceType,image(source,thumb-360))',
                'fromPractice(id,name,practiceType,email,image(source,thumb-360))',
                'facilityServiceTests(id,name,group(id,name))',
                'stageInfo(stage,nextAction,hoursLeft,urgency,providersWithSlots,providersWithoutSlots,totalProviders,pendingProviders,externalRecipientsCount,targetPracticeType,additionalRecordsRequests)',
                'timeframe',
                'diagnosis',
                'customDate',
                'speciality(id,name)',
                'insurancePlan(id,name)',
                'followUpReferral',
            ]
        ).join(',')

        const params = {
            _fields,
            ...query,
        }
        if (params.state === 'all') delete params.state

        return axios.get('referrals-v2', { params })
    },

    get(referralId, physicianId, signature, fields) {
        const _fields = (fields || referralFields).join(',')
        const route = signature ? `unauthenticated/referrals/${referralId}` : `referrals/${referralId}`
        return axios.get(route, { params: { _fields, physicianId, signature } })
    },

    create(data) {
        return axios.post('referrals', data)
    },

    createToOutsider(data) {
        return axios.post('referrals/outsider', data)
    },

    accept(referralId, fields) {
        const _fields = (fields || referralFields).join(',')
        return axios.post(`referrals/${referralId}/accept`, null, { params: { _fields } }).then(updateStoreMembers)
    },

    decline(referralId, physicianId, closeReason, comment, fields) {
        const _fields = (fields || referralFields).join(',')
        return axios
            .post(`referrals/${referralId}/decline/${physicianId}`, { closeReason, comment }, { params: { _fields } })
            .then(updateStoreMembers)
    },

    reschedule(referralId) {
        return axios.post(`referrals/${referralId}/reschedule`).then(updateStoreMembers)
    },

    cancel(isUserPracticeOther, referralId, physicianId, payload, fields) {
        const _fields = (fields || referralFields).join(',')
        return axios
            .post(`referrals/${referralId}/cancel/${physicianId}`, payload, { params: { _fields } })
            .then(isUserPracticeOther ? null : updateStoreMembers)
    },

    updateInsuranceAuthorization(referralId, action, physicianId) {
        return axios
            .put(`referrals/${referralId}/insurance-authorization`, {
                referralId,
                action,
                physicianId,
            })
            .then(updateStoreMembers)
    },

    updateInsuranceAuthorizationFromSignature(referralId, action, signature, insuranceAuthNumber) {
        return axios.put(`unauthenticated/referrals/${referralId}/insurance-authorization`, {
            signature,
            referralId,
            action,
            insuranceAuthNumber,
        })
    },

    getForReschedule(referralId, fields) {
        const _fields = (
            fields || [
                'id',
                'referredBy',
                'insurancePlanId',
                'specialityId',
                'subSpecialityId',
                'referredTo',
                'practice(id,practiceType,zip)',
                'practiceId',
                'practiceLocationId',
            ]
        ).join(',')

        return axios.get(`referrals/${referralId}/for-reschedule`, { params: { _fields } })
    },

    getDataForDirectReferral(toUserId) {
        return axios.get(`referrals/for-direct-referral/${toUserId}`)
    },

    updateAvailabilitySlot(referralId, slot) {
        return axios.put(`referrals/${referralId}/availability-slot`, {
            date: slot.date,
            startTime: slot.startTime,
        })
    },

    updatePatientVisitStatus(referralId, status) {
        return axios.post(`referrals/${referralId}/patient-visit`, {
            status,
            _fields: 'status,insuranceAuthorizationObtainedDateTime',
        })
    },

    uploadConsultationNotes(files, referralId) {
        return Promise.all(
            files.map((file) => {
                const body = file.uploadKey
                    ? {
                          note: file.file.name,
                          uploadKey: file.uploadKey,
                          fileName: file.file.name,
                          size: Math.round((file.fileSize || file.file.size) / 1024),
                          referralId,
                      }
                    : {
                          note: file.file.name,
                          file: file.base64File,
                          size: Math.round(file.fileSize / 1024),
                          referralId,
                      }

                return axios
                    .post(`/referrals/upload/consultation-note`, body, {
                        onUploadProgress: function (progressEvent) {
                            if (!progressEvent.total || file.uploadKey) return
                            var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            file.progress = percentCompleted
                        },
                    })
                    .then((res) => {
                        file.isUploaded = true
                        file.file.id = res.id
                    })
                    .catch((err) => {
                        file.errorMessage = err.response?.data?.message || i18n.t('common.upload.unsupported')
                    })
            }),
        )
    },

    removeConsultationReport(reportId) {
        return axios.post(`/referrals/${reportId}/remove-consultation-report`)
    },

    sendConsultationReport(referralId, consultationNotesAlreadyUploaded = false) {
        return axios.post(`/referrals/${referralId}/send-consultation-report`, {
            consultationNotesAlreadyUploaded,
        })
    },

    checkSignatureValidity(rId, s, ts) {
        return axios.get(`unauthenticated/reviews/referrals/${rId}/${s}/${ts}`)
    },

    updatePhysicianReview(referralId, practiceType, grades) {
        const payload = Object.assign({}, grades)
        return axios.put(`reviews/referrals/${referralId}/physician-review/practice-type/${practiceType}`, payload)
    },

    updatePatientReview(referralId, practiceType, grades, s, ts) {
        const payload = Object.assign({}, grades)
        payload.token = s
        payload.timestamp = ts
        return axios.post(`unauthenticated/reviews/referrals/${referralId}/patient-review/practice-type/${practiceType}`, payload)
    },

    getConsultationNotes(referralId) {
        return axios.get(`referrals/${referralId}/consultation-reports`)
    },

    getMatchesFromNetwork(physicianId, text, practiceType, limit) {
        return axios.get(`referrals/matches-from-network/${physicianId}`, {
            params: {
                practiceType,
                text,
                limit,
            },
        })
    },

    acceptDisclaimer(referralId, fields) {
        const _fields = (fields || referralFields).join(',')
        return axios.post(`referrals/${referralId}/accept-disclaimer`, null, { params: { _fields } })
    },

    requestConsultationReport(referralId, message) {
        return axios.post(`referrals/${referralId}/request-report`, { message })
    },

    getAnalyticReportData(userId, fromDate, toDate) {
        return axios.get(`referrals/report-data`, {
            params: {
                userId,
                fromDate,
                toDate,
            },
        })
    },

    createEprescribes(request) {
        return axios.post('eprescriptions', request)
    },

    sendProposedTimeSlots(referralId, data) {
        return axios.put(`/referrals/${referralId}/propose-availability-slot`, data)
    },
    additionalRecordRequest(referralPoolId, data) {
        return axios.post(`/referrals/${referralPoolId}/additional-records-request`, data)
    },
}
